/**
 * Escuta pelo OpenAI Realtime via WebRTC.
 *
 * É o que o ADR-007 comprou do Realtime e que não temos: VAD, detecção
 * semântica de fim de turno, barge-in e transcrição de qualidade. No modo
 * `hybrid` a saída de áudio da sessão fica desligada — quem fala é o
 * OmniVoice. No modo `realtime` a mesma sessão também fala, e por isso este
 * módulo expõe `speak`/`hush` além do papel de ouvinte.
 *
 * A negociação SDP passa pelo nosso servidor (`POST /v1/u1/realtime/calls`):
 * a chave da OpenAI não desce para o navegador, e o servidor pode recusar com
 * 503 quando não há chave configurada — que é o sinal para o engine cair no
 * `listenBrowser`.
 *
 * Desde a v0.2 essa chamada é autenticada e amarrada a uma sessão: vai por
 * `authFetch` (que anexa o token) e com `?session=<id>`. O servidor procura a
 * sessão dentro do tenant do token, então um id de sessão de outro cliente
 * simplesmente não existe para esta chamada — e minuto faturado na OpenAI
 * passa a ter dono identificável.
 */

import { authFetch } from '../auth';
import { getSession } from '../client/session';
import {
  DEFAULT_LANGUAGE,
  DEFAULT_REALTIME_MODEL,
  DEFAULT_REALTIME_VOICE,
  ICE_TIMEOUT_MS,
  REALTIME_CALLS_URL,
} from './config';
import { abrirMicrofone, criarMedidorDeNivel, fecharStream } from './mic';
import type { MedidorDeNivel } from './mic';
import { ehEco } from './turno';
import type { HeardHandler, LevelHandler, Listener, Speaker } from './types';
import { RealtimeUnavailableError, comoErro } from './types';

export interface RealtimeOptions {
  callsUrl?: string;
  model?: string;
  language?: string;
  /**
   * `true` só no modo `realtime`. Em `hybrid` e `text` a sessão escuta e
   * transcreve, mas não emite áudio (ADR-007).
   */
  audioOutput?: boolean;
  voice?: string;
  onError?: (erro: Error) => void;
}

/** A sessão Realtime é ouvinte e, quando `audioOutput`, também é falante. */
export type RealtimeSession = Listener & Speaker;

/**
 * Evento do canal de dados. Campos declarados são os que consumimos; o resto
 * fica em `unknown` porque a API evolui e não queremos tipar o que ignoramos.
 */
interface EventoRealtime {
  type?: string;
  delta?: string;
  transcript?: string;
  error?: { message?: string; code?: string };
  [chave: string]: unknown;
}

/**
 * Espera o ICE fechar antes de mandar o offer. A API Realtime não aceita
 * trickle ICE: um offer sem candidatos negocia e depois não conecta. O tempo
 * limite existe porque uma rede ruim pode nunca chegar a `complete`, e um
 * offer com os candidatos que já temos ainda tem chance.
 */
async function esperarIce(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return;
  await new Promise<void>((resolve) => {
    const concluir = (): void => {
      clearTimeout(limite);
      pc.removeEventListener('icegatheringstatechange', aoMudar);
      resolve();
    };
    const aoMudar = (): void => {
      if (pc.iceGatheringState === 'complete') concluir();
    };
    const limite = setTimeout(concluir, ICE_TIMEOUT_MS);
    pc.addEventListener('icegatheringstatechange', aoMudar);
  });
}

export function createRealtimeListener(opts: RealtimeOptions = {}): RealtimeSession {
  const callsUrl = opts.callsUrl ?? REALTIME_CALLS_URL;
  const model = opts.model ?? DEFAULT_REALTIME_MODEL;
  const idioma = opts.language ?? DEFAULT_LANGUAGE;
  const comAudio = opts.audioOutput ?? false;
  const voz = opts.voice ?? DEFAULT_REALTIME_VOICE;

  const ouvintesTexto: HeardHandler[] = [];
  const ouvintesNivel: LevelHandler[] = [];
  const ouvintesBargeIn: Array<() => void> = [];

  let pc: RTCPeerConnection | null = null;
  let canal: RTCDataChannel | null = null;
  let stream: MediaStream | null = null;
  let medidor: MedidorDeNivel | null = null;
  let remoto: HTMLAudioElement | null = null;

  let textoFalado: string | null = null;
  let parcial = '';
  let encerrarFala: (() => void) | null = null;

  const enviar = (evento: Record<string, unknown>): void => {
    if (canal?.readyState !== 'open') return;
    canal.send(JSON.stringify(evento));
  };

  const emitir = (texto: string, final: boolean): void => {
    const limpo = texto.trim();
    if (!limpo) return;
    for (const cb of ouvintesTexto) cb(limpo, final);
  };

  const fecharFala = (): void => {
    const fecha = encerrarFala;
    encerrarFala = null;
    fecha?.();
  };

  const configurarSessao = (): void => {
    enviar({
      type: 'session.update',
      session: {
        type: 'realtime',
        // Em `hybrid` e `text` a sessão nunca deve gerar áudio: a voz é do
        // Diego. Desligar aqui, e não silenciar o alto-falante, evita pagar
        // geração de áudio que ninguém vai ouvir.
        output_modalities: comAudio ? ['audio'] : ['text'],
        audio: {
          input: {
            transcription: { model: 'gpt-4o-transcribe', language: idioma.slice(0, 2) },
            // VAD semântico: é a peça que o navegador não tem — ele decide o
            // fim do turno pelo sentido da frase, não só pelo silêncio.
            //
            // `create_response` acompanha o modo, e o padrão da API é `true`:
            // omitir aqui deixava o modelo gerando resposta em `hybrid`, onde
            // quem responde é o cérebro e quem fala é o Diego.
            turn_detection: { type: 'semantic_vad', create_response: comAudio },
          },
          output: { voice: voz },
        },
      },
    });
  };

  const aoEvento = (bruto: string): void => {
    let evento: EventoRealtime;
    try {
      evento = JSON.parse(bruto) as EventoRealtime;
    } catch {
      return;
    }

    switch (evento.type) {
      /**
       * BARGE-IN. Este é o evento real de início de fala do usuário.
       *
       * Não existe `response.cancelled` na API Realtime — quem procurar por
       * ele vai esperar para sempre por um evento que o servidor nunca emite,
       * e o barge-in simplesmente não acontece. O par verdadeiro é
       * `input_audio_buffer.speech_started` / `speech_stopped`.
       */
      case 'input_audio_buffer.speech_started':
        parcial = '';
        if (textoFalado !== null) {
          // Aqui não dá para checar eco: ainda não há transcrição, só a
          // detecção acústica. Confiamos no cancelamento de eco do navegador
          // e cortamos na hora — barge-in tardio é pior que barge-in falso,
          // e a guarda de eco abaixo impede que o texto ecoado vire turno.
          for (const cb of ouvintesBargeIn) cb();
        }
        break;

      case 'input_audio_buffer.speech_stopped':
        break;

      case 'conversation.item.input_audio_transcription.delta': {
        const delta = evento.delta ?? '';
        if (!delta) break;
        parcial += delta;
        if (textoFalado !== null && ehEco(parcial, textoFalado)) {
          parcial = '';
          break;
        }
        emitir(parcial, false);
        break;
      }

      case 'conversation.item.input_audio_transcription.completed': {
        const texto = evento.transcript ?? parcial;
        parcial = '';
        if (textoFalado !== null && ehEco(texto, textoFalado)) break;
        emitir(texto, true);
        break;
      }

      // `response.done` fecha a geração; no WebRTC o áudio ainda pode estar
      // no buffer de saída, e `output_audio_buffer.stopped` é o fim de fato.
      case 'response.done':
        if (!comAudio) fecharFala();
        break;

      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
        fecharFala();
        break;

      case 'error':
        opts.onError?.(new Error(evento.error?.message ?? 'Erro na sessão Realtime.'));
        break;

      default:
        break;
    }
  };

  const start = async (): Promise<void> => {
    if (pc) return;

    stream = await abrirMicrofone();
    medidor = criarMedidorDeNivel(stream, (nivel) => {
      for (const cb of ouvintesNivel) cb(nivel);
    });

    const conexao = new RTCPeerConnection();
    pc = conexao;

    // O canal de dados precisa existir antes do offer para entrar no SDP.
    canal = conexao.createDataChannel('oai-events');
    canal.onopen = configurarSessao;
    canal.onmessage = (ev: MessageEvent<string>): void => aoEvento(ev.data);

    for (const trilha of stream.getAudioTracks()) conexao.addTrack(trilha, stream);

    // Mesmo sem áudio de saída declaramos a recepção: a negociação da OpenAI
    // espera uma m-line de áudio bidirecional. O elemento fica mudo quando o
    // modo não é `realtime`.
    remoto = new Audio();
    remoto.autoplay = true;
    remoto.muted = !comAudio;
    conexao.ontrack = (ev: RTCTrackEvent): void => {
      if (remoto) remoto.srcObject = ev.streams[0];
    };

    const offer = await conexao.createOffer();
    await conexao.setLocalDescription(offer);
    await esperarIce(conexao);

    // A sessão do U1 já existe quando o microfone liga (o App a cria no
    // bootstrap). Sem ela não há a que amarrar a chamada, e mandar a oferta
    // assim mesmo só produziria um 400 depois de gastar o ICE.
    const sessaoU1 = getSession();
    if (sessaoU1 === null) {
      stop();
      throw new RealtimeUnavailableError(
        'A sessão do U1 ainda não existe: a chamada de voz precisa de uma sessão para ser autorizada.',
      );
    }
    const urlDaChamada = `${callsUrl}?session=${encodeURIComponent(sessaoU1.session_id)}`;

    let resposta: Response;
    try {
      resposta = await authFetch(urlDaChamada, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({
          sdp: conexao.localDescription?.sdp ?? offer.sdp ?? '',
          model,
          voice: voz,
          language: idioma,
          audio_output: comAudio,
        }),
      });
    } catch (e) {
      stop();
      throw new RealtimeUnavailableError(
        `Não foi possível falar com ${callsUrl}: ${comoErro(e).message}`,
      );
    }

    if (resposta.status === 503) {
      stop();
      // Contrato com o servidor: 503 significa "Realtime indisponível agora"
      // (sem chave, sem cota, upstream fora). O chamador cai no listenBrowser.
      throw new RealtimeUnavailableError(
        'O Realtime está indisponível (503). A escuta cai para o navegador.',
      );
    }

    if (!resposta.ok) {
      stop();
      throw new Error(`A negociação do Realtime falhou com ${resposta.status}.`);
    }

    const tipo = resposta.headers.get('content-type') ?? '';
    let sdpResposta: string;
    if (tipo.includes('application/sdp')) {
      sdpResposta = await resposta.text();
    } else {
      const corpo = (await resposta.json()) as { sdp?: string };
      sdpResposta = corpo.sdp ?? '';
    }

    if (!sdpResposta) {
      stop();
      throw new Error('A negociação do Realtime voltou sem SDP de resposta.');
    }

    await conexao.setRemoteDescription({ type: 'answer', sdp: sdpResposta });
  };

  const stop = (): void => {
    fecharFala();
    if (canal) {
      canal.onopen = null;
      canal.onmessage = null;
      try {
        canal.close();
      } catch {
        // canal já fechado
      }
      canal = null;
    }
    if (pc) {
      pc.ontrack = null;
      pc.close();
      pc = null;
    }
    if (remoto) {
      remoto.srcObject = null;
      remoto = null;
    }
    medidor?.parar();
    medidor = null;
    fecharStream(stream);
    stream = null;
    parcial = '';
    textoFalado = null;
  };

  /**
   * Fala pelo próprio Realtime (modo `realtime`).
   *
   * O modelo é speech-to-speech e não tem um "diga este texto"; o caminho é
   * pedir uma resposta com instrução literal. A promessa fecha em
   * `output_audio_buffer.stopped`, que é quando o áudio realmente acabou.
   */
  const speak = async (text: string): Promise<void> => {
    const limpo = text.trim();
    if (!limpo || !comAudio) return;
    hush();
    enviar({
      type: 'response.create',
      response: {
        output_modalities: ['audio'],
        instructions: `Diga exatamente o texto a seguir, sem acrescentar, resumir ou comentar: ${limpo}`,
      },
    });
    await new Promise<void>((resolve) => {
      encerrarFala = resolve;
    });
  };

  const hush = (): void => {
    // `response.cancel` interrompe a geração no servidor;
    // `output_audio_buffer.clear` descarta o que já foi enviado e ainda está
    // na fila de reprodução do WebRTC. Sem o segundo, o Will continua falando
    // por segundos depois de ter sido interrompido.
    enviar({ type: 'response.cancel' });
    enviar({ type: 'output_audio_buffer.clear' });
    fecharFala();
  };

  return {
    start,
    stop,
    speak,
    hush,
    onHeard: (cb) => void ouvintesTexto.push(cb),
    onLevel: (cb) => void ouvintesNivel.push(cb),
    onBargeIn: (cb) => void ouvintesBargeIn.push(cb),
    setTalking: (talking, text) => {
      textoFalado = talking ? (text ?? '') : null;
    },
  };
}
